/**
 * Emission-factor granularity helpers.
 *
 * An emission factor is a "default value" when its granularity is fallback data
 * rather than measured data for a specific installation. The backend expresses
 * this through the granularity field: 'Global' and 'Country' are both defaults.
 *
 * This predicate is duplicated inline in ~40 call sites across the codebase.
 * That duplication is what let the Suppliers emissions table drift into
 * checking only 'Global', which rendered 'Country' defaults with the green
 * "actual values" check mark. Prefer importing this over re-inlining the list.
 */

export const DEFAULT_GRANULARITIES = ['Global', 'Country'];

/**
 * @param {string|null|undefined} granularity - The emission factor granularity.
 * @returns {boolean} True when the factor is a default (non-actual) value.
 */
export const isDefaultGranularity = (granularity) => DEFAULT_GRANULARITIES.includes(granularity);

/**
 * Inverse of isDefaultGranularity, but requires a known granularity: a missing
 * granularity is not positive evidence of actual data.
 *
 * @param {string|null|undefined} granularity - The emission factor granularity.
 * @returns {boolean} True when the factor is measured, actual data.
 */
export const isActualGranularity = (granularity) =>
  Boolean(granularity) && !isDefaultGranularity(granularity);

/**
 * A supplier-level factor names a supplier but no installation. Importers can no
 * longer create one, and editing one means naming one of that supplier's
 * installations (backend docs/features/uk-cbam-emission-factors, D20/D24).
 *
 * @param {string|null|undefined} granularity - The emission factor granularity.
 * @returns {boolean} True when the factor is supplier-level.
 */
export const isSupplierLevelGranularity = (granularity) => granularity === 'Supplier';
